import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, Tooltip, Collapse,
  CircularProgress, Skeleton, Alert, InputAdornment, Grid, CssBaseline,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CalculateIcon from '@mui/icons-material/Calculate';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HalfIcon from '@mui/icons-material/Brightness5';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import Header from '../Includes/Header';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import AppToast from '../../components/Toast/AppToast';
import { retrieveEmployeesDetails } from '../Apihelper/ApiEmployeesDetails';
import { getLeaveDays, setLeaveDays } from '../Apihelper/AttendanceHelper';
import { getHolidays } from '../Apihelper/HolidayApi';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ─── Styled ───────────────────────────────────────────────────────────────────

const HeadCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  fontWeight: 600,
  fontSize: 13,
  whiteSpace: 'nowrap',
}));

const BodyRow = styled(TableRow)(() => ({
  '&:nth-of-type(odd)': { backgroundColor: '#f9fafb' },
  '&:hover': { backgroundColor: 'rgba(27,67,50,0.05)' },
}));

const StatBox = ({ label, value, color, bg = '#f0fdf4' }) => {
  const { colors: appColors } = useAppTheme();
  return (
    <Box sx={{ textAlign: 'center', px: 2, py: 1.5, bgcolor: bg, borderRadius: 2, minWidth: 80 }}>
      <Typography sx={{ fontWeight: 700, fontSize: 20, color: color || appColors.evergreen }}>{value}</Typography>
      <Typography sx={{ fontSize: 11, color: '#6b7280', mt: 0.2 }}>{label}</Typography>
    </Box>
  );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const parseTimeToMinutes = (timeStr) => {
  if (!timeStr) return null;
  const s = String(timeStr).trim().toUpperCase()
    .replace(/^(\d{1,2})\.(\d{2})\s*(AM|PM)/i, '$1:$2$3');
  // HH:MM:SS or HH:MM (24h)
  let m = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?(?:\s*(AM|PM))?$/);
  if (m && !m[3]) return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
  // HH:MM AM/PM
  m = s.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/);
  if (m) {
    let h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    if (m[3] === 'PM' && h !== 12) h += 12;
    if (m[3] === 'AM' && h === 12) h = 0;
    return h * 60 + min;
  }
  // H AM/PM
  m = s.match(/^(\d{1,2})\s*(AM|PM)$/);
  if (m) {
    let h = parseInt(m[1], 10);
    if (m[2] === 'PM' && h !== 12) h += 12;
    if (m[2] === 'AM' && h === 12) h = 0;
    return h * 60;
  }
  return null;
};

// Slot string like "9:00 AM - 1:00 PM" → hours worked
const slotHours = (slotStr) => {
  if (!slotStr) return null;
  const normalized = String(slotStr).replace(/[–—]/g, '-');
  const parts = normalized.split(/\s*-\s*/).map((p) => p.trim()).filter(Boolean);
  if (parts.length < 2) return null;
  const start = parseTimeToMinutes(parts[0]);
  const end = parseTimeToMinutes(parts[1]);
  if (start == null || end == null || end <= start) return null;
  return (end - start) / 60;
};

// Minutes-of-day → "1:00 PM" display string
const formatMinutesToTime = (min) => {
  if (min == null) return null;
  const h24 = Math.floor(min / 60) % 24;
  const m = min % 60;
  const period = h24 >= 12 ? 'PM' : 'AM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${period}`;
};

// Slot string like "9:00 AM - 1:00 PM" → { startMin, endMin } in minutes-of-day
const slotWindow = (slotStr) => {
  if (!slotStr) return null;
  const normalized = String(slotStr).replace(/[–—]/g, '-');
  const parts = normalized.split(/\s*-\s*/).map((p) => p.trim()).filter(Boolean);
  if (parts.length < 2) return null;
  const startMin = parseTimeToMinutes(parts[0]);
  const endMin = parseTimeToMinutes(parts[1]);
  if (startMin == null || endMin == null || endMin <= startMin) return null;
  return { startMin, endMin };
};

const workingDaysInMonth = (yearMonth) => {
  const [y, mo] = yearMonth.split('-').map(Number);
  const total = new Date(y, mo, 0).getDate();
  let count = 0;
  for (let d = 1; d <= total; d++) {
    const day = new Date(y, mo - 1, d).getDay();
    if (day !== 0) count++; // exclude Sundays
  }
  return count;
};

const monthLabel = (ym) => {
  const [y, mo] = ym.split('-');
  return new Date(Number(y), Number(mo) - 1, 1)
    .toLocaleString('default', { month: 'long', year: 'numeric' });
};

const fmt2 = (n) => Number(n ?? 0).toFixed(2);

// ─── Fetch monthly attendance ─────────────────────────────────────────────────

const fetchMonthlyAttendance = async (token, userId, month) => {
  const myHeaders = new Headers();
  myHeaders.append('Content-Type', 'application/json');
  myHeaders.append('Authorization', `Bearer ${token}`);
  const res = await fetch(`${window.constants.api_url}/monthly-attendance`, {
    method: 'POST',
    headers: myHeaders,
    body: JSON.stringify({ user_id: userId, month, limit: 500, index: 0 }),
  });
  return res.json();
};

// ─── Day classification ────────────────────────────────────────────────────

// rec.date is "DD-MM-YYYY" (backend's date('d-m-Y', ...) format).
const isSunday = (dateStr) => {
  if (!dateStr) return false;
  const [d, m, y] = dateStr.split('-').map(Number);
  if (!d || !m || !y) return false;
  return new Date(y, m - 1, d).getDay() === 0;
};

// "DD-MM-YYYY" -> "YYYY-MM-DD" so it can be looked up in a holidayMap keyed
// by the backend's ISO date format.
const toIsoDate = (dateStr) => {
  if (!dateStr) return null;
  const [d, m, y] = dateStr.split('-');
  if (!d || !m || !y) return null;
  return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
};

const getHolidayName = (dateStr, holidayMap) => {
  if (!holidayMap) return null;
  const iso = toIsoDate(dateStr);
  return iso ? holidayMap[iso] || null : null;
};

// A day counts as fully worked only when the employee attends EVERY slot
// they're assigned (1 slot for single-shift staff, both morning + evening
// for split-shift staff) and their first check-in of the day is on time.
// Attending only some of their assigned slots (e.g. morning but not evening)
// or arriving late is a half day; attending none (no check_in at all) is
// absent. Sundays and admin-listed holidays are a paid day off — never
// absent/half regardless of punches.
// Returns 'full' | 'half' | 'sunday' | 'holiday' | null (null = no check-in at all).
const getDayType = (rec, emp, excuseMinutes, holidayMap) => {
  if (isSunday(rec.date)) return 'sunday';
  if (getHolidayName(rec.date, holidayMap)) return 'holiday';
  if (!rec.check_in) return null;
  const checkInMin = parseTimeToMinutes(rec.check_in);
  if (checkInMin === null) return null;

  const slots = [emp?.morning_slot, emp?.evening_slot]
    .map((s) => slotWindow(s))
    .filter(Boolean);

  if (slots.length <= 1) {
    // Single slot (or no slot data to work with) — unchanged behavior.
    return checkInMin <= excuseMinutes ? 'full' : 'half';
  }

  // Multi-slot: count how many of the assigned slots were actually attended.
  const punchInMinutes = (rec.punches || [])
    .filter((p) => p.type === 'in')
    .map((p) => parseTimeToMinutes(p.time))
    .filter((m) => m !== null);
  if (!punchInMinutes.includes(checkInMin)) punchInMinutes.push(checkInMin);

  const slotsAttended = slots.filter(({ startMin, endMin }) => {
    const windowStart = startMin - 30; // 30 min grace before slot start, same as AutoCheckoutEmployees
    return punchInMinutes.some((m) => m >= windowStart && m <= endMin);
  }).length;

  if (slotsAttended >= slots.length && checkInMin <= excuseMinutes) return 'full';
  if (slotsAttended > 0) return 'half';
  return null; // attended none of the assigned slots — treat as absent
};

// For a split-shift employee, break the day's punches into a Morning session
// and an Evening session (each its own check-in/check-out) instead of
// collapsing the whole day into one check_in→check_out span. Single-slot
// employees just get one unlabeled session (unchanged display).
const getSessionsForDay = (rec, emp) => {
  const namedSlots = [
    { label: 'Morning', window: slotWindow(emp?.morning_slot) },
    { label: 'Evening', window: slotWindow(emp?.evening_slot) },
  ].filter((s) => s.window);

  if (namedSlots.length <= 1) {
    return [{ label: null, checkIn: rec.check_in || null, checkOut: rec.check_out || null }];
  }

  // Pair the day's punches into sessions in chronological order.
  const sessions = [];
  let openIn = null;
  for (const p of rec.punches || []) {
    if (p.type === 'in') {
      if (openIn) sessions.push({ in: openIn, out: null });
      openIn = p.time;
    } else if (p.type === 'out' && openIn) {
      sessions.push({ in: openIn, out: p.time });
      openIn = null;
    }
  }
  if (openIn) sessions.push({ in: openIn, out: null });
  if (sessions.length === 0 && rec.check_in) {
    sessions.push({ in: rec.check_in, out: rec.check_out });
  }

  return namedSlots.map(({ label, window }) => {
    const session = sessions.find((s) => {
      const m = parseTimeToMinutes(s.in);
      return m !== null && m >= window.startMin - 30 && m <= window.endMin;
    });
    if (!session) return { label, checkIn: null, checkOut: null };
    // No explicit "out" punch for this session — this is exactly the case
    // where the work-hours calc falls back to crediting up to the slot's
    // official end, so show that same time here instead of a blank dash.
    const checkOut = session.out || formatMinutesToTime(window.endMin);
    const isEstimated = !session.out;
    return { label, checkIn: session.in, checkOut, isEstimated };
  });
};

// ─── Calc salary ──────────────────────────────────────────────────────────────

const calcSalary = (records, excuseMinutes, salaryPerDay, totalWorkingDays, emp = {}, leaveDays = 0, holidayMap = null) => {
  let fullDays = 0, halfDays = 0;
  for (const rec of records) {
    const type = getDayType(rec, emp, excuseMinutes, holidayMap);
    if (type === 'full') fullDays++;
    else if (type === 'half') halfDays++;
    // 'sunday', 'holiday', and null (no punches) fall through uncounted —
    // both are already excluded from totalWorkingDays, so absentDays stays correct.
  }
  const rawAbsentDays = Math.max(totalWorkingDays - fullDays - halfDays, 0);
  // Admin-approved leave (vacation/sick) excuses absent days — paid like a
  // full day — but can never excuse more than the employee was actually
  // absent for.
  const excusedLeaveDays = Math.min(Math.max(leaveDays, 0), rawAbsentDays);
  const absentDays = rawAbsentDays - excusedLeaveDays;
  const effectiveDays = fullDays + halfDays * 0.5 + excusedLeaveDays;
  const earnedSalary = effectiveDays * salaryPerDay;
  return { fullDays, halfDays, absentDays, excusedLeaveDays, effectiveDays, earnedSalary };
};

// ─── Employee Row ─────────────────────────────────────────────────────────────

const EmployeeRow = ({ emp, month, globalExcuseMinutes, totalWorkingDays, token, onGeneratePdf, showToast, holidayMap }) => {
  const { colors: appColors } = useAppTheme();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState(null);
  const [stats, setStats] = useState(null);
  const [leaveDays, setLeaveDaysState] = useState(0);
  const [leaveInput, setLeaveInput] = useState('0');
  const [savingLeave, setSavingLeave] = useState(false);
  const fetched = useRef(false);

  const [yearNum, monthNum] = month.split('-').map((s) => parseInt(s, 10));

  // Per-employee excuse time: use employee's own excuse_time, fall back to global
  const empExcuseMinutes = parseTimeToMinutes(emp.excuse_time) ?? globalExcuseMinutes;

  // Per-employee salary per day = monthly salary / working days
  const monthlySalary = parseFloat(emp.salary) || 0;
  const salaryPerDay = totalWorkingDays > 0 ? monthlySalary / totalWorkingDays : 0;

  // Slot hours info
  const morningHrs = slotHours(emp.morning_slot);
  const eveningHrs = slotHours(emp.evening_slot);
  const totalSlotHrs = (morningHrs ?? 0) + (eveningHrs ?? 0);

  const load = useCallback(async () => {
    if (fetched.current) return;
    fetched.current = true;
    setLoading(true);
    try {
      const empId = emp.user_id ?? emp.id;
      const [attRes, leaveRes] = await Promise.all([
        fetchMonthlyAttendance(token, empId, month),
        getLeaveDays(token, empId, yearNum, monthNum).catch(() => null),
      ]);
      const data = Array.isArray(attRes.data) ? attRes.data : [];
      const fetchedLeave = leaveRes?.code === 200 ? (leaveRes.leave_days ?? 0) : 0;
      setRecords(data);
      setLeaveDaysState(fetchedLeave);
      setLeaveInput(String(fetchedLeave));
      setStats(calcSalary(data, empExcuseMinutes, salaryPerDay, totalWorkingDays, emp, fetchedLeave, holidayMap));
    } catch {
      setRecords([]);
      setStats(calcSalary([], empExcuseMinutes, salaryPerDay, totalWorkingDays, emp, 0, holidayMap));
    } finally {
      setLoading(false);
    }
  }, [token, emp, month, yearNum, monthNum, empExcuseMinutes, salaryPerDay, totalWorkingDays, holidayMap]);

  useEffect(() => {
    if (records !== null) {
      setStats(calcSalary(records, empExcuseMinutes, salaryPerDay, totalWorkingDays, emp, leaveDays, holidayMap));
    }
  }, [empExcuseMinutes, salaryPerDay, totalWorkingDays, records, emp, leaveDays, holidayMap]);

  const handleToggle = () => {
    setOpen((p) => !p);
    load();
  };

  const handleSaveLeaveDays = async () => {
    const parsed = Math.max(0, Math.min(31, parseInt(leaveInput, 10) || 0));
    setLeaveInput(String(parsed));
    if (parsed === leaveDays) return;
    setSavingLeave(true);
    try {
      const empId = emp.user_id ?? emp.id;
      const res = await setLeaveDays(token, empId, yearNum, monthNum, parsed);
      if (res.code === 200) {
        setLeaveDaysState(parsed);
        showToast?.(`Leave days updated for ${emp.name}`);
      } else {
        showToast?.(res.message || 'Failed to save leave days', 'error');
      }
    } catch {
      showToast?.('Failed to save leave days', 'error');
    } finally {
      setSavingLeave(false);
    }
  };

  return (
    <>
      <BodyRow>
        <TableCell sx={{ width: 40, pr: 0 }}>
          <IconButton size="small" onClick={handleToggle}>
            {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{emp.name}</Typography>
          <Typography sx={{ fontSize: 11, color: '#6b7280' }}>{emp.designation || '—'}</Typography>
        </TableCell>
        <TableCell>
          <Typography sx={{ fontSize: 13 }}>₹{monthlySalary.toLocaleString()}</Typography>
          <Typography sx={{ fontSize: 11, color: '#6b7280' }}>₹{fmt2(salaryPerDay)}/day</Typography>
        </TableCell>
        <TableCell>
          <Typography sx={{ fontSize: 12, color: '#374151' }}>
            {emp.excuse_time || emp.morning_slot
              ? (emp.excuse_time || '—')
              : '—'}
          </Typography>
          {totalSlotHrs > 0 && (
            <Typography sx={{ fontSize: 11, color: '#6b7280' }}>{totalSlotHrs}h/day</Typography>
          )}
        </TableCell>
        <TableCell>
          {loading ? <Skeleton width={50} /> : stats ? (
            <Chip label={stats.fullDays} size="small" sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 600 }} />
          ) : '—'}
        </TableCell>
        <TableCell>
          {loading ? <Skeleton width={50} /> : stats ? (
            <Chip label={stats.halfDays} size="small" sx={{ bgcolor: '#fef9c3', color: '#854d0e', fontWeight: 600 }} />
          ) : '—'}
        </TableCell>
        <TableCell>
          {loading ? <Skeleton width={50} /> : stats ? (
            <Chip label={stats.absentDays} size="small" sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontWeight: 600 }} />
          ) : '—'}
        </TableCell>
        <TableCell>
          {loading ? <Skeleton width={80} /> : stats ? (
            <Typography sx={{ fontWeight: 700, color: appColors.evergreen, fontSize: 15 }}>
              ₹{fmt2(stats.earnedSalary)}
            </Typography>
          ) : '—'}
        </TableCell>
        <TableCell>
          {stats && (
            <Tooltip title="Download Salary Slip">
              <IconButton size="small" onClick={() => onGeneratePdf(emp, stats, records, salaryPerDay, empExcuseMinutes, holidayMap)} sx={{ color: appColors.evergreen }}>
                <PictureAsPdfIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
      </BodyRow>

      {/* Expandable detail */}
      <TableRow>
        <TableCell colSpan={9} sx={{ p: 0, border: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 2, bgcolor: '#f8fafc', borderRadius: 2, p: 2.5, border: '1px solid #e2e8f0' }}>

              {/* Employee info chips */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                {emp.morning_slot && (
                  <Chip size="small" label={`Morning: ${emp.morning_slot}`} sx={{ bgcolor: '#f0fdf4', color: '#166534', fontSize: 11 }} />
                )}
                {emp.evening_slot && (
                  <Chip size="small" label={`Evening: ${emp.evening_slot}`} sx={{ bgcolor: '#eff6ff', color: '#1e40af', fontSize: 11 }} />
                )}
                {emp.excuse_time && (
                  <Chip size="small" icon={<AccessTimeIcon sx={{ fontSize: 12 }} />} label={`Excuse: ${emp.excuse_time}`} sx={{ bgcolor: '#fef9c3', color: '#854d0e', fontSize: 11 }} />
                )}
              </Box>

              {/* Leave days — admin-approved leave excused from absent count */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Leave Days:</Typography>
                <TextField
                  size="small"
                  type="number"
                  value={leaveInput}
                  onChange={(e) => setLeaveInput(e.target.value)}
                  onBlur={handleSaveLeaveDays}
                  inputProps={{ min: 0, max: 31, style: { padding: '4px 8px', width: 50 } }}
                  disabled={savingLeave}
                />
                {savingLeave && <CircularProgress size={14} />}
                <Typography sx={{ fontSize: 11, color: '#6b7280' }}>
                  (excused from absent, paid as full day)
                </Typography>
              </Box>

              {/* Stats */}
              {stats && (
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
                  <StatBox label="Monthly Salary" value={`₹${monthlySalary.toLocaleString()}`} color={appColors.evergreen} bg="#f0fdf4" />
                  <StatBox label="Salary/Day" value={`₹${fmt2(salaryPerDay)}`} color="#0f766e" bg="#f0fdfa" />
                  <StatBox label="Full Days" value={stats.fullDays} color="#15803d" bg="#f0fdf4" />
                  <StatBox label="Half Days" value={stats.halfDays} color="#92400e" bg="#fefce8" />
                  <StatBox label="Absent" value={stats.absentDays} color="#991b1b" bg="#fff1f2" />
                  {stats.excusedLeaveDays > 0 && (
                    <StatBox label="Leave (Excused)" value={stats.excusedLeaveDays} color="#7c3aed" bg="#f5f3ff" />
                  )}
                  <StatBox label="Eff. Days" value={fmt2(stats.effectiveDays)} color="#1e40af" bg="#eff6ff" />
                  <StatBox label="Earned" value={`₹${fmt2(stats.earnedSalary)}`} color={appColors.evergreen} bg="#dcfce7" />
                </Box>
              )}

              {/* Day-by-day table */}
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} sx={{ color: appColors.evergreen }} />
                </Box>
              ) : records && records.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#e2e8f0' }}>
                      {['Date', 'Check In', 'Check Out', 'Work Hours', 'Day Type'].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 600, fontSize: 12 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {records.map((rec, i) => {
                      const dayType = getDayType(rec, emp, empExcuseMinutes, holidayMap);
                      const isSundayRow = dayType === 'sunday';
                      const isHolidayRow = dayType === 'holiday';
                      const isOffRow = isSundayRow || isHolidayRow;
                      const holidayName = isHolidayRow ? getHolidayName(rec.date, holidayMap) : null;
                      const isLate = dayType === 'half';
                      const hasCheckIn = dayType !== null;
                      const sessions = isOffRow ? [] : getSessionsForDay(rec, emp);
                      return (
                        <TableRow key={i} sx={{ '&:nth-of-type(odd)': { bgcolor: '#f9fafb' } }}>
                          <TableCell sx={{ fontSize: 12 }}>{rec.date}</TableCell>
                          <TableCell sx={{ fontSize: 12, color: isLate ? '#b45309' : 'inherit', fontWeight: isLate ? 600 : 400 }}>
                            {isOffRow ? '—' : sessions.map((s, si) => (
                              <Box key={si} sx={{ whiteSpace: 'nowrap' }}>
                                {s.label && <Typography component="span" sx={{ fontSize: 10, color: '#6b7280', mr: 0.5 }}>{s.label}:</Typography>}
                                {s.checkIn || '—'}
                              </Box>
                            ))}
                            {isLate && <Typography component="span" sx={{ fontSize: 10, color: '#b45309', ml: 0.5 }}>(late)</Typography>}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12 }}>
                            {isOffRow ? '—' : sessions.map((s, si) => (
                              <Box key={si} sx={{ whiteSpace: 'nowrap' }}>
                                {s.checkOut || '—'}
                                {s.isEstimated && s.checkOut && (
                                  <Typography component="span" sx={{ fontSize: 10, color: '#6b7280', ml: 0.5 }}>(est.)</Typography>
                                )}
                              </Box>
                            ))}
                          </TableCell>
                          <TableCell sx={{ fontSize: 12 }}>{isOffRow ? '—' : (rec.work_hours || '—')}</TableCell>
                          <TableCell>
                            {isSundayRow ? (
                              <Chip label="Sunday" size="small" sx={{ bgcolor: '#e0e7ff', color: '#3730a3', fontSize: 11 }} />
                            ) : isHolidayRow ? (
                              <Chip label={holidayName || 'Holiday'} size="small" sx={{ bgcolor: '#fae8ff', color: '#86198f', fontSize: 11 }} />
                            ) : !hasCheckIn ? (
                              <Chip icon={<CancelOutlinedIcon />} label="Absent" size="small" sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontSize: 11 }} />
                            ) : isLate ? (
                              <Chip icon={<HalfIcon />} label="Half Day" size="small" sx={{ bgcolor: '#fef9c3', color: '#854d0e', fontSize: 11 }} />
                            ) : (
                              <Chip icon={<CheckCircleOutlineIcon />} label="Full Day" size="small" sx={{ bgcolor: '#dcfce7', color: '#166534', fontSize: 11 }} />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <Typography sx={{ color: '#6b7280', fontSize: 13, textAlign: 'center', py: 1 }}>
                  No attendance records for this month.
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const SalaryGeneration = () => {
  const { colors: appColors } = useAppTheme();
  const navigate = useNavigate();
  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  const [month, setMonth] = useState(defaultMonth);
  const [globalExcuseTime, setGlobalExcuseTime] = useState('10:00');
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [calculated, setCalculated] = useState(false);
  const [token, setToken] = useState('');
  const [adminId, setAdminId] = useState('');
  const [openToast, setOpenToast] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [toastSeverity, setToastSeverity] = useState('success');
  const [holidayMap, setHolidayMap] = useState({});

  const globalExcuseMinutes = parseTimeToMinutes(globalExcuseTime) ?? 600;
  // Working days = total days minus Sundays minus admin-listed holidays that
  // don't already fall on a Sunday (both are paid — never double-deducted).
  const [y, mo] = month.split('-').map(Number);
  const totalDays = new Date(y, mo, 0).getDate();
  let sundays = 0;
  for (let d = 1; d <= totalDays; d++) {
    if (new Date(y, mo - 1, d).getDay() === 0) sundays++;
  }
  const holidaysThisMonth = Object.keys(holidayMap).filter((iso) => {
    const [hy, hm] = iso.split('-').map(Number);
    if (hy !== y || hm !== mo) return false;
    const day = new Date(iso).getDay();
    return day !== 0; // don't double-count a holiday that's already a Sunday
  }).length;
  const totalWorkingDays = totalDays - sundays - holidaysThisMonth;

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await getHolidays(token, y);
        if (res.code === 200 && Array.isArray(res.data)) {
          const map = {};
          res.data.forEach((h) => { map[h.date] = h.name; });
          setHolidayMap(map);
        }
      } catch {
        // Non-fatal — salary calc just proceeds without holiday exclusions.
      }
    })();
  }, [token, y]);

  const showToast = (msg, sev = 'success') => {
    setToastMsg(msg);
    setToastSeverity(sev);
    setOpenToast(true);
  };

  useEffect(() => {
    const userData = localStorage.getItem('userdata');
    if (!userData) { navigate('/'); return; }
    const obj = JSON.parse(userData);
    setToken(obj.token);
    setAdminId(obj.id);
  }, []);

  const loadEmployees = async () => {
    setLoadingEmployees(true);
    setCalculated(false);
    setEmployees([]);
    try {
      const res = await retrieveEmployeesDetails(token, adminId, '', 200, 0);
      const data = Array.isArray(res.data) ? res.data : [];
      setEmployees(data);
      setCalculated(true);
      if (data.length === 0) showToast('No employees found.', 'warning');
    } catch {
      showToast('Failed to load employees.', 'error');
    } finally {
      setLoadingEmployees(false);
    }
  };

  const handleGeneratePdf = (emp, stats, records, salaryPerDay, empExcuseMinutes, pdfHolidayMap) => {
    const isSplitShift = Boolean(emp.morning_slot && emp.evening_slot);
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFontSize(18);
    doc.setTextColor(27, 67, 50);
    doc.text('SALARY SLIP', pageWidth / 2, 16, { align: 'center' });
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(monthLabel(month), pageWidth / 2, 22, { align: 'center' });

    doc.setFontSize(10);
    doc.setTextColor(30);
    let y = 32;
    doc.text(`Employee: ${emp.name}`, 14, y);
    doc.text(`Designation: ${emp.designation || '—'}`, pageWidth - 14, y, { align: 'right' });
    y += 6;
    doc.text(`Phone: ${emp.phone || '—'}`, 14, y);
    doc.text(`Excuse Time: ${emp.excuse_time || globalExcuseTime}`, pageWidth - 14, y, { align: 'right' });
    y += 6;
    doc.text(`Morning Slot: ${emp.morning_slot || '—'}`, 14, y);
    doc.text(`Evening Slot: ${emp.evening_slot || '—'}`, pageWidth - 14, y, { align: 'right' });
    y += 8;

    autoTable(doc, {
      startY: y,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [27, 67, 50] },
      head: [['Monthly Salary', 'Working Days', 'Salary/Day', 'Full', 'Half', 'Leave', 'Absent', 'Eff. Days', 'Earned']],
      body: [[
        `₹${parseFloat(emp.salary || 0).toLocaleString()}`,
        totalWorkingDays,
        `₹${fmt2(salaryPerDay)}`,
        stats.fullDays,
        stats.halfDays,
        stats.excusedLeaveDays || 0,
        stats.absentDays,
        fmt2(stats.effectiveDays),
        `₹${fmt2(stats.earnedSalary)}`,
      ]],
    });

    const attendanceHead = isSplitShift
      ? [['Date', 'Morning In', 'Morning Out', 'Evening In', 'Evening Out', 'Work Hrs', 'Day Type']]
      : [['Date', 'Check In', 'Check Out', 'Work Hrs', 'Day Type']];

    const attendanceBody = (records || []).map((r) => {
      const dayType = getDayType(r, emp, empExcuseMinutes, pdfHolidayMap);
      const typeLabel = dayType === 'sunday' ? 'Sunday'
        : dayType === 'holiday' ? (getHolidayName(r.date, pdfHolidayMap) || 'Holiday')
        : dayType === 'full' ? 'Full Day' : dayType === 'half' ? 'Half Day' : 'Absent';
      if (dayType === 'sunday' || dayType === 'holiday') {
        return isSplitShift
          ? [r.date, '—', '—', '—', '—', '—', typeLabel]
          : [r.date, '—', '—', '—', typeLabel];
      }
      const sessions = getSessionsForDay(r, emp);
      if (isSplitShift) {
        const morning = sessions.find((s) => s.label === 'Morning') || {};
        const evening = sessions.find((s) => s.label === 'Evening') || {};
        return [
          r.date,
          morning.checkIn || '—',
          morning.checkOut ? `${morning.checkOut}${morning.isEstimated ? ' (est.)' : ''}` : '—',
          evening.checkIn || '—',
          evening.checkOut ? `${evening.checkOut}${evening.isEstimated ? ' (est.)' : ''}` : '—',
          r.work_hours || '—',
          typeLabel,
        ];
      }
      const single = sessions[0] || {};
      return [
        r.date,
        single.checkIn || '—',
        single.checkOut ? `${single.checkOut}${single.isEstimated ? ' (est.)' : ''}` : '—',
        r.work_hours || '—',
        typeLabel,
      ];
    });

    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 6,
      theme: 'striped',
      styles: { fontSize: 8, cellPadding: 1.8 },
      headStyles: { fillColor: [27, 67, 50] },
      head: attendanceHead,
      body: attendanceBody,
    });

    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text(
      `Generated on: ${new Date().toLocaleString()}`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 8,
      { align: 'center' }
    );

    doc.save(`salary_${emp.name?.replace(/\s+/g, '_')}_${month}.pdf`);
    showToast(`Salary slip downloaded for ${emp.name}`);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f0f2f5' }}>
      <CssBaseline />
      <Box><Header /></Box>

      <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, sm: 3, md: 4 }, mt: { xs: 8, sm: 9 }, minWidth: 0, overflow: 'hidden' }}>

        {/* Page Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <IconButton onClick={() => navigate(-1)} size="small"
            sx={{ bgcolor: 'white', border: '1px solid #e2e8f0', color: appColors.evergreen, '&:hover': { bgcolor: '#f0fdf4' } }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827', lineHeight: 1.2 }}>
              Employee Salary Generation
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#6b7280', mt: 0.3 }}>
              Salary & excuse time pulled automatically from each employee's profile
            </Typography>
          </Box>
        </Box>

        {/* Config Card */}
        <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', mb: 3, border: '1px solid #e2e8f0' }}>
          <Box sx={{
            px: 3, py: 2.5,
            display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2,
            minWidth: 0, overflow: 'hidden',
          }}>
            {/* Month */}
            <TextField
              label="Month"
              type="month"
              value={month}
              onChange={(e) => { setMonth(e.target.value); setCalculated(false); }}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{
                width: 180,
                '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: appColors.evergreen } },
                '& .MuiInputLabel-root.Mui-focused': { color: appColors.evergreen },
              }}
            />

            {/* Divider */}
            <Box sx={{ width: 1, height: 36, bgcolor: '#e2e8f0', display: { xs: 'none', sm: 'block' } }} />

            {/* Excuse Time */}
            <Box>
              <TextField
                label="Default Excuse Time"
                type="time"
                value={globalExcuseTime}
                onChange={(e) => setGlobalExcuseTime(e.target.value)}
                size="small"
                InputLabelProps={{ shrink: true }}
                InputProps={{ startAdornment: <InputAdornment position="start"><AccessTimeIcon sx={{ fontSize: 15, color: '#b45309' }} /></InputAdornment> }}
                sx={{
                  width: 200,
                  '& .MuiOutlinedInput-root': { '&.Mui-focused fieldset': { borderColor: '#b45309' } },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#b45309' },
                }}
              />
              <Typography sx={{ fontSize: 11, color: '#9ca3af', mt: 0.3, ml: 0.5 }}>
                Per-employee overrides this
              </Typography>
            </Box>

            {/* Divider */}
            <Box sx={{ width: 1, height: 36, bgcolor: '#e2e8f0', display: { xs: 'none', sm: 'block' } }} />

            {/* Working Days badge */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, bgcolor: '#eff6ff', borderRadius: 2, border: '1px solid #bfdbfe' }}>
              <Box>
                <Typography sx={{ fontSize: 10, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>Working Days</Typography>
                <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#1e40af', lineHeight: 1.1 }}>{totalWorkingDays}</Typography>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                <Chip size="small" label={`${totalDays} total`} sx={{ fontSize: 10, height: 18, bgcolor: '#dbeafe', color: '#1e40af' }} />
                <Chip size="small" label={`−${sundays} Sun`} sx={{ fontSize: 10, height: 18, bgcolor: '#fee2e2', color: '#991b1b' }} />
              </Box>
            </Box>

            {/* Rules pills */}
            <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', flex: 1 }}>
              {[
                { label: '✓ Full Day', sub: '≤ excuse time', bg: '#dcfce7', color: '#166534' },
                { label: '½ Half Day', sub: 'after excuse time', bg: '#fef9c3', color: '#854d0e' },
                { label: '✕ Absent', sub: 'no check-in → ₹0', bg: '#fee2e2', color: '#991b1b' },
              ].map((r) => (
                <Box key={r.label} sx={{ px: 1.5, py: 0.8, bgcolor: r.bg, borderRadius: 1.5 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: r.color }}>{r.label}</Typography>
                  <Typography sx={{ fontSize: 10, color: r.color, opacity: 0.8 }}>{r.sub}</Typography>
                </Box>
              ))}
            </Box>

            {/* Calculate */}
            <Button
              variant="contained"
              onClick={loadEmployees}
              disabled={loadingEmployees}
              startIcon={loadingEmployees ? <CircularProgress size={15} color="inherit" /> : <CalculateIcon />}
              sx={{
                bgcolor: appColors.evergreen, '&:hover': { bgcolor: '#14532D' },
                px: 3, py: 1, fontWeight: 600, borderRadius: 2,
                whiteSpace: 'nowrap',
                boxShadow: '0 2px 8px rgba(27,67,50,0.25)',
              }}
            >
              {loadingEmployees ? 'Loading…' : 'Calculate'}
            </Button>
          </Box>
        </Paper>

        {/* Results Table */}
        {calculated && employees.length > 0 && (
          <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', overflow: 'hidden', minWidth: 0 }}>
            {/* Table header bar */}
            <Box sx={{
              px: 3, py: 2,
              background: `linear-gradient(135deg, ${appColors.evergreen} 0%, ${appColors.leaf} 100%)`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <Box>
                <Typography sx={{ color: 'white', fontWeight: 700, fontSize: 15 }}>
                  {employees.length} Employees · {monthLabel(month)}
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: 12, mt: 0.2 }}>
                  {totalWorkingDays} working days · click ▾ row to see daily breakdown
                </Typography>
              </Box>
              <Chip
                label={`${totalWorkingDays} days`}
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 600 }}
              />
            </Box>

            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <HeadCell sx={{ width: 40, bgcolor: '#f8fafc', color: '#374151' }} />
                    {['Name', 'Salary', 'Excuse Time', 'Full Days', 'Half Days', 'Absent', 'Earned Salary', 'Slip'].map((h) => (
                      <HeadCell key={h}>{h}</HeadCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employees.map((emp, i) => (
                    <EmployeeRow
                      key={emp.id ?? i}
                      emp={emp}
                      month={month}
                      globalExcuseMinutes={globalExcuseMinutes}
                      totalWorkingDays={totalWorkingDays}
                      token={token}
                      onGeneratePdf={handleGeneratePdf}
                      showToast={showToast}
                      holidayMap={holidayMap}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {calculated && employees.length === 0 && !loadingEmployees && (
          <Paper elevation={0} sx={{ borderRadius: 3, p: 4, textAlign: 'center', border: '1px solid #e2e8f0' }}>
            <Typography sx={{ color: '#6b7280', fontSize: 15 }}>No employees found in the system.</Typography>
          </Paper>
        )}
      </Box>

      <AppToast open={openToast} message={toastMsg} severity={toastSeverity} onClose={() => setOpenToast(false)} />
    </Box>
  );
};

export default SalaryGeneration;
